import React from 'react';
import { motion } from 'framer-motion';
import { Search, RefreshCw, Plus } from 'lucide-react';
import { useBuildStacksStore } from '../../stores/useBuildStacksStore.js';

const BuildStackList: React.FC = () => {
  const { 
    activeStackId, 
    searchQuery, 
    categoryFilter,
    isLoading,
    error,
    setActive, 
    setSearchQuery, 
    setCategoryFilter,
    refresh,
    getFilteredStacks
  } = useBuildStacksStore();
  
  const filteredStacks = getFilteredStacks();

  const handleCreateNew = () => {
    setActive(undefined);
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Sticky Header with Search and Refresh */}
      <header className="sticky top-0 z-10 bg-gradient-to-r from-blue-700 to-transparent border-b border-gray-700/50 shadow-sm">
        <div className="relative p-3 overflow-hidden">
          {/* Smoked Opaque Overlay */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
          
          {/* Content on top of overlay */}
          <div className="relative z-10 flex items-center justify-between">
            <h2 className="text-white font-semibold text-base drop-shadow-lg">BuildStacks</h2>
            <div className="flex items-center space-x-3">
              <div className="relative flex-1 min-w-0 max-w-48">
                <Search className="absolute left-2 top-2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search BuildStacks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 bg-slate-800/50 border border-slate-600 rounded text-sm text-slate-200 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 transition-all duration-100"
                />
              </div>
              <RefreshCw 
                data-testid="refresh-icon"
                className={`h-5 w-5 text-white cursor-pointer hover:text-gray-200 transition-colors drop-shadow-md ${
                  isLoading ? 'animate-spin' : ''
                }`}
                onClick={refresh}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Underlined Tab Navigation */}
      <div className="px-3 py-2 bg-gray-800 border-b border-gray-700">
        {/* Subtle blue divider */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-blue-500/10"></div>
        <nav className="flex gap-4">
          {[
            { key: 'all', label: 'All BuildStacks' },
            { key: 'pre-made', label: 'Pre-made' },
            { key: 'client', label: 'Client' },
            { key: 'community', label: 'Community' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setCategoryFilter(key as 'all' | 'pre-made' | 'client' | 'community')}
              className={`relative py-2 text-sm border-b-2 transition-all duration-100 cursor-pointer ${
                categoryFilter === key
                  ? 'text-blue-400 border-blue-400'
                  : 'text-slate-400 border-transparent hover:text-slate-200 hover:brightness-110'
              }`}
            >
              {label}
              {categoryFilter === key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 transition-all duration-100 opacity-100"></div>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Full-width Create New BuildStack Button */}
      <div className="px-3 py-4 bg-gray-800 border-b border-gray-700">
        <button
          onClick={handleCreateNew}
          className="relative w-full flex items-center justify-center space-x-2 p-3 bg-gradient-to-r from-blue-700 to-transparent border border-blue-500/30 rounded-lg hover:from-blue-600 hover:to-transparent hover:brightness-110 transition-all duration-100 focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-100"></div>
          <Plus className="w-4 h-4 text-blue-400 relative z-10 transition-all duration-100" />
          <span className="font-medium text-blue-400 relative z-10 transition-all duration-100">Create New BuildStack</span>
        </button>
      </div>

      {/* Scrollable List Container */}
      <div className="flex-1 overflow-y-auto p-2">
        
        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-400 text-sm drop-shadow-sm">Loading BuildStacks...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-8">
            <div className="text-red-500 mb-2 text-sm drop-shadow-md">Error loading BuildStacks</div>
            <div className="text-gray-600 text-sm mb-4 drop-shadow-sm">{error}</div>
            <button 
              onClick={refresh}
              className="px-4 py-2 bg-gradient-to-r from-blue-700 to-transparent text-white text-sm rounded-md hover:from-blue-600 hover:to-transparent transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Content - only show when not loading and no error */}
        {!isLoading && !error && (
          <>
        {/* BuildStack Cards */}
        {filteredStacks.map((buildStack) => (
          <motion.div
            key={buildStack.id}
            className={`relative p-3 rounded-lg shadow hover:shadow-lg cursor-pointer transition-all duration-200 overflow-hidden ${
              activeStackId === buildStack.id
                ? 'bg-gradient-to-r from-blue-600/30 to-teal-500/30 border border-blue-500/50'
                : buildStack.category === 'pre-made'
                  ? 'bg-gray-900 hover:bg-gray-700 border-l-4 border-l-purple-500 border border-gray-700'
                  : buildStack.category === 'client'
                    ? 'bg-gray-900 hover:bg-gray-700 border-l-4 border-l-blue-500 border border-gray-700'
                    : 'bg-gray-900 hover:bg-gray-700 border-l-4 border-l-green-500 border border-gray-700'
            }`}
            whileHover={{ scale: 1.02 }}
            onClick={() => setActive(buildStack.id)}
          >
            {/* Smoked Opaque Overlay for active gradient cards */}
            {activeStackId === buildStack.id && (
              <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
            )}
            
            <div className="relative z-10">
              {/* Mini-gradient accent bar */}
              <div className="relative w-full h-1 bg-gradient-to-r from-blue-700 to-transparent rounded-full mb-2 overflow-hidden">
                {/* Smoked overlay for gradient accent bar */}
                <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
              </div>
              
              <div className="font-medium text-white text-sm drop-shadow-lg">{buildStack.name}</div>
              <div className="text-sm text-gray-400 mt-1 drop-shadow-sm">
                {buildStack.description || `Last updated: ${new Date(buildStack.updatedAt).toLocaleDateString()}`}
              </div>
              
              {buildStack.steps && buildStack.steps.length > 0 && (
                <div className="text-xs text-slate-500 mt-2 drop-shadow-sm">
                  {buildStack.steps.length} step{buildStack.steps.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {filteredStacks.length === 0 && searchQuery && (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-2 text-sm drop-shadow-sm">No BuildStacks found</div>
            <div className="text-gray-600 text-sm drop-shadow-sm">Try adjusting your search criteria</div>
          </div>
        )}
        
        </>
        )}
      </div>

      {/* Footer with Last Updated */}
      <div className="px-3 py-2 bg-gray-800 border-t border-gray-700">
        <div className="text-xs text-slate-500 opacity-50">
          Last updated: {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

export default BuildStackList;
